import { useMemo } from 'react'

export type AbTestGroup = 'A' | 'B'

interface UsePhoneAbTestGroupOptions {
  percentualGrupoA?: number
  salt?: string
  greenListPhones?: string[]
}

const normalizePhone = (phone: string): string => phone.replace(/\D/g, '')

const getSafePercentual = (percentualGrupoA: number): number =>
  Math.min(100, Math.max(0, percentualGrupoA))

const hashPhone = (phone: string, salt = ''): number => {
  const input = normalizePhone(phone) + salt
  let hash = 5381

  for (let index = 0; index < input.length; index += 1) {
    hash = (hash * 33 + input.charCodeAt(index)) % 0x80000000
  }

  return hash
}

const getABGroup = (
  phone: string,
  percentualGrupoA: number,
  salt: string,
  greenListPhones: string[],
): AbTestGroup => {
  const normalizedPhone = normalizePhone(phone)

  if (!normalizedPhone) {
    return 'A'
  }

  const isGreenListed = greenListPhones
    .map((greenListPhone) => normalizePhone(greenListPhone))
    .includes(normalizedPhone)

  if (isGreenListed) {
    return 'A'
  }

  const hash = hashPhone(normalizedPhone, salt)
  const bucket = hash % 100

  return bucket < getSafePercentual(percentualGrupoA) ? 'A' : 'B'
}

export const usePhoneAbTestGroup = (
  phone: string | undefined,
  options: UsePhoneAbTestGroupOptions = {},
): AbTestGroup => {
  const { percentualGrupoA = 100, salt = 'insurance-cross-selling', greenListPhones = [] } = options

  return useMemo(
    () => getABGroup(phone ?? '', percentualGrupoA, salt, greenListPhones),
    [percentualGrupoA, phone, salt, greenListPhones],
  )
}
